import type { SF_MODAL_COMPONENTS } from '../../constants';

const ACCORDION_INDICES = {
  PROPERTY_INFO: 0,
  RENT_DETAILS: 1,
  AMENITIES: 3,
  FINAL_DETAILS: 6,
} as const;

export type ErrorKey =
  | 'beds'
  | 'baths'
  | 'roomDescription'
  | 'monthlyRent'
  | 'rentDetailsPets'
  | 'interiorAmenities'
  | 'screeningCriteriaPetsAllowed'
  | 'contactEmail'
  | 'contactName'
  | 'contactPhone'
  | 'forRentBy'
  | 'isApplicationsAccepted'
  | 'leaseDuration'
  | 'r4rPermissions'
  | 'touring';

export const GRANULAR_ERROR_KEYS = new Set<string>([
  'beds',
  'baths',
  'roomDescription',
  'monthlyRent',
  'rentDetailsPets',
  'interiorAmenities',
  'screeningCriteriaPetsAllowed',
  'contactEmail',
  'contactName',
  'contactPhone',
  'forRentBy',
  'isApplicationsAccepted',
  'leaseDuration',
  'r4rPermissions',
  'touring',
]);

export type ErrorMapping = {
  accordionIndex: number;
  componentKey: keyof typeof SF_MODAL_COMPONENTS | 'TouringSection' | null;
  sectionName: string;
  errorMessage: string;
};

/**
 * Maps an error key to its UI location (accordion index, component key) and display message.
 *
 * @param errorKey - The validation error identifier
 * @param options - Context needed for conditional mappings (e.g., room for rent vs standard listing)
 * @returns Error mapping object or null if the error key is unknown
 */
export const getErrorMapping = (
  errorKey: ErrorKey,
  options: {
    isRoomForRent?: boolean;
  } = {},
): ErrorMapping | null => {
  const { isRoomForRent = false } = options;

  switch (errorKey) {
    case 'beds':
      return {
        accordionIndex: ACCORDION_INDICES.PROPERTY_INFO,
        componentKey: 'BedroomsFormField',
        sectionName: 'Property information',
        errorMessage: 'Missing bedrooms details',
      };

    case 'baths':
      return {
        accordionIndex: ACCORDION_INDICES.PROPERTY_INFO,
        componentKey: 'BathroomsFormField',
        sectionName: 'Property information',
        errorMessage: 'Missing bathroom details',
      };

    case 'roomDescription':
      return {
        accordionIndex: ACCORDION_INDICES.PROPERTY_INFO,
        componentKey: 'PropertyDescriptionFormField',
        sectionName: 'Property information',
        errorMessage: 'Missing room description',
      };

    case 'monthlyRent':
      return {
        accordionIndex: ACCORDION_INDICES.RENT_DETAILS,
        componentKey: 'MonthlyRentFormField',
        sectionName: 'Rent details',
        errorMessage: 'Missing monthly rent',
      };

    case 'rentDetailsPets':
      return {
        accordionIndex: ACCORDION_INDICES.RENT_DETAILS,
        componentKey: 'PetPolicyFormField',
        sectionName: 'Rent details',
        errorMessage: 'Missing pet policy',
      };

    case 'interiorAmenities':
      return {
        accordionIndex: ACCORDION_INDICES.AMENITIES,
        componentKey: 'AmenitiesInteriorFormField',
        sectionName: 'Amenities',
        errorMessage: 'Missing amenities details',
      };

    case 'screeningCriteriaPetsAllowed':
      return {
        accordionIndex: ACCORDION_INDICES.AMENITIES,
        componentKey: 'PetsAllowedFormField',
        sectionName: 'Pets',
        errorMessage: 'Missing pets allowed',
      };

    case 'contactEmail':
      return {
        accordionIndex: ACCORDION_INDICES.FINAL_DETAILS,
        componentKey: 'EmailFormField',
        sectionName: 'Final details',
        errorMessage: 'Missing email address',
      };

    case 'contactName':
      return {
        accordionIndex: ACCORDION_INDICES.FINAL_DETAILS,
        componentKey: 'NameFormField',
        sectionName: 'Final details',
        errorMessage: 'Missing contact name',
      };

    case 'contactPhone':
      return {
        accordionIndex: ACCORDION_INDICES.FINAL_DETAILS,
        componentKey: 'PhoneNumberFormField',
        sectionName: 'Final details',
        errorMessage: 'Missing phone number',
      };

    case 'forRentBy':
      return {
        accordionIndex: ACCORDION_INDICES.FINAL_DETAILS,
        componentKey: isRoomForRent ? 'RoomForRentByFormField' : 'ForRentByFieldSet',
        sectionName: 'Final details',
        errorMessage: 'Missing listing provider',
      };

    case 'isApplicationsAccepted':
      return {
        accordionIndex: ACCORDION_INDICES.FINAL_DETAILS,
        componentKey: 'AcceptZillowApplicationsFormField',
        sectionName: 'Final details',
        errorMessage: 'Missing application fee',
      };

    case 'leaseDuration':
      return {
        accordionIndex: ACCORDION_INDICES.FINAL_DETAILS,
        componentKey: 'LeaseDurationFormField',
        sectionName: 'Final details',
        errorMessage: 'Missing lease duration',
      };

    case 'r4rPermissions':
      return {
        accordionIndex: ACCORDION_INDICES.FINAL_DETAILS,
        componentKey: 'RoomForRentByFormField',
        sectionName: 'Final details',
        errorMessage: 'Missing permissions',
      };

    case 'touring':
      return {
        accordionIndex: ACCORDION_INDICES.FINAL_DETAILS,
        componentKey: 'TouringSection',
        sectionName: 'Final details',
        errorMessage: 'Missing tour availability',
      };

    default:
      // Fallback for unknown error keys
      return {
        accordionIndex: 0,
        componentKey: null,
        sectionName: 'Unknown section',
        errorMessage: 'Required field missing',
      };
  }
};
/**
 * Converts requiredFieldsErrors object to an ordered array of error keys.
 *
 * The order follows the accordion structure on the review page:
 * beds → baths → roomDescription → monthlyRent → rentDetailsPets → amenities → screeningCriteria → contact fields.
 *
 * @param requiredFieldsErrors - Object mapping error keys to boolean flags indicating if the error exists
 * @returns Array of error keys that are currently true, in UI order
 */
export const getOrderedErrorKeys = (requiredFieldsErrors: Record<string, boolean>): Array<ErrorKey> => {
  const granularContactKeys: Array<ErrorKey> = [
    'contactEmail',
    'contactName',
    'contactPhone',
    'forRentBy',
    'isApplicationsAccepted',
    'leaseDuration',
    'r4rPermissions',
    'touring',
  ];

  const errorOrder: Array<ErrorKey> = [
    'beds',
    'baths',
    'roomDescription',
    'monthlyRent',
    'rentDetailsPets',
    'interiorAmenities',
    'screeningCriteriaPetsAllowed',
    ...granularContactKeys,
  ];

  return errorOrder.filter((key) => requiredFieldsErrors[key] === true);
};
